import { Card, RankBadge, Skeleton } from '@strategy4kids/ui';
import { useCallback, useEffect, useState } from 'react';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { useT } from '../i18n/useT.js';
import { type LeaderboardRow, api } from '../services/api.js';

export interface LeaderboardProps {
  readonly onExit: () => void;
}

export function Leaderboard({ onExit }: LeaderboardProps) {
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useT('navigation');
  const { t: tCommon } = useT('common');

  const load = useCallback(() => {
    setRows(null);
    setError(null);
    api
      .fetchLeaderboard(20)
      .then(setRows)
      .catch(() => setError('Could not load the leaderboard.'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ScreenLayout width="wide" data-testid="leaderboard" data-appdom-scope="leaderboard">
      <PageHeader
        title={t('top-players')}
        action={
          <button
            type="button"
            data-testid="leaderboard-exit"
            onClick={onExit}
            className="min-h-[48px] rounded-xl border border-border bg-surface-raised px-6 py-2 font-display text-lg font-semibold text-fg shadow-sm transition-colors hover:bg-accent-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {tCommon('back')}
          </button>
        }
      />
      {error && (
        <Card
          className={`${PANEL_CLASS} mx-auto flex w-full max-w-2xl flex-col items-center gap-3 p-6 text-center`}
          data-testid="leaderboard-error-state"
        >
          <p className="font-bold text-red-600 dark:text-red-400" data-testid="leaderboard-error">
            {error}
          </p>
          <p className="text-base text-fg-muted">Check your connection, then try again.</p>
          <button
            type="button"
            onClick={load}
            className="min-h-[48px] rounded-xl bg-accent px-6 py-2 font-display text-base font-semibold text-accent-fg shadow-sm transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Retry
          </button>
        </Card>
      )}
      {rows === null && !error && (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-2" data-testid="leaderboard-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}
      {rows && rows.length === 0 && (
        <Card
          className={`${PANEL_CLASS} mx-auto flex w-full max-w-2xl flex-col items-center gap-1 p-8 text-center`}
          data-testid="leaderboard-empty"
        >
          <p className="font-display text-h3 font-bold text-fg">No rated games yet</p>
          <p className="text-base text-fg-muted">Play a rated game to appear here.</p>
        </Card>
      )}
      {rows && rows.length > 0 && (
        <ol className="mx-auto flex w-full max-w-4xl flex-col gap-3">
          {rows.map((row, i) => {
            const rank = i + 1;
            const isPodium = rank <= 3;
            return (
              <li
                key={row.nickname}
                data-testid={`leaderboard-row-${i}`}
                // `data-appdom-id` is the identity app-dom indexes on:
                // INSTRUMENTED_SELECTOR is `[data-appdom-id], [data-appdom]`, so
                // the `data-appdom-type` below was inert without it — the element
                // was never discovered, so nothing read its type.
                data-appdom-id={`leaderboard-row-${i}`}
                data-appdom-type="ListItem"
                className={`flex items-center justify-between rounded-2xl border border-border bg-surface/90 p-4 shadow-sm backdrop-blur-sm lift lg:p-5 ${
                  isPodium ? 'ring-2 ring-accent/40' : ''
                }`}
              >
                <span className="flex items-center gap-3">
                  <RankBadge rank={rank} size={isPodium ? 'lg' : 'md'} />
                  <span className="text-lg font-bold text-fg lg:text-xl">{row.nickname}</span>
                </span>
                <span
                  className="font-display text-3xl font-bold text-accent lg:text-4xl"
                  data-testid={`leaderboard-rating-${i}`}
                >
                  {Math.round(row.rating)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </ScreenLayout>
  );
}
